import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { MerakisService } from '@app/features/merakis/shared/merakis.service';
import { FortigatesService } from '@app/features/fortigates/shared/fortigates.service';
import { CradlepointsService } from '@app/features/cradlepoints/shared/cradlepoints.service';
import { MistService } from '@app/features/mists/shared/mist.service';
import { ChainWideMerakisService } from '@app/features/merakis/shared/chain-wide-merakis.service';
import { ChainWideFortigatesService } from '@app/features/fortigates/shared/chain-wide-fortigates.service';
import { ChainWideCradlepointsService } from '@app/features/cradlepoints/shared/chain-wide-cradlepoints.service';
import { ChainWideMistsService } from '@app/features/mists/shared/chain-wide-mists.service';
import { Restaurant } from '@app/shared/models/restaurant.model';
import { Location } from '@app/shared/models/location.model';
import { Cradlepoint } from '@app/shared/models/cradlepoint/cradlepoint.model';
import { MerakiSwitchStatus } from '@app/shared/models/meraki/meraki-switch-status.model';
import { Fortigate } from '@app/shared/models/fortigate/fortigate.model';
import { MerakiLocation } from '@app/shared/models/meraki/meraki-location.model';
import { FortigateLocation } from '@app/shared/models/fortigate/fortigate-location.model';
import { CradlepointLocation } from '@app/shared/models/cradlepoint/cradlepoint-location.model';
import { MistLocation } from '@app/shared/models/mist/mist-location';
import * as L from 'leaflet';
import { firstValueFrom } from 'rxjs';
import {
    NETWORK_STATE,
    NetworkState,
    NetworkStateEvaluation,
    buildNetworkStateEvaluation,
    evaluateNetworkState
} from '@app/shared/utils/network-thresholds';

@Component({
    selector: 'app-restaurant',
    templateUrl: './chain-health.component.html',
    styleUrls: ['./chain-health.component.css'],
    standalone: false
})
export class ChainHealthComponent implements OnInit {
    @ViewChild('restaurantsTable') restaurantTable: MatTable<Restaurant>;
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatSlideToggleModule, { static: true }) slideTogle: MatSlideToggleModule;
    flip = false;
    private map;
    private markerLayerGroup;
    isLoading = false;
    hideLabs: boolean;
    hideFutures: boolean;
    hideRemodels: boolean;
    showOnBackup: boolean;
    showDualLte: boolean;
    showHardDown: boolean;
    filterText: string = null;
    merakiLocations: MerakiLocation[] = [];
    fortigateLocations: FortigateLocation[] = [];
    cradlepointLocations: CradlepointLocation[] = [];
    mistLocations: MistLocation[] = [];
    locations = [];
    restaurants = [];
    displayedColumns = ['storeNumber', 'locationName', 'locationTimeZone', 'locationStatus', 'networkState', 'merakis', 'fortigates', 'cradlepoints', 'mists'];
    dataSource = new MatTableDataSource<Restaurant>(this.restaurants);
    readonly NETWORK_STATE = NETWORK_STATE;

    constructor(
        public locationsService: LocationsService,
        public merakisService: MerakisService,
        public fortigatesService: FortigatesService,
        public cradlepointsService: CradlepointsService,
        public mistsService: MistService,
        private chainWideMerakisService: ChainWideMerakisService,
        private chainWideFortigatesService: ChainWideFortigatesService,
        private chainWideCradlepointsService: ChainWideCradlepointsService,
        private chainWideMistsService: ChainWideMistsService
    ) { }

    ngOnInit() {
        this.initComponent();
    }

    private async initComponent() {
        this.isLoading = true;
        await this.getAllLocations();
        const [merakiLocations, fortigateLocations, cradlepointLocations, mistLocations] = await Promise.all([
            this.chainWideMerakisService.createDataSource(this.locations),
            this.chainWideFortigatesService.createDataSource(this.locations),
            this.chainWideCradlepointsService.createDataSource(this.locations),
            this.chainWideMistsService.createDataSource(this.locations),
        ]);
        this.merakiLocations = merakiLocations;
        this.fortigateLocations = fortigateLocations;
        this.cradlepointLocations = cradlepointLocations;
        this.mistLocations = mistLocations;
        this.createDataSource();
        this.dataSource.paginator = this.paginator;
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'storeNumber': return item.location.number;
                case 'locationName': return item.location.name;
                case 'locationTimeZone': return item.location.timezone;
                case 'locationStatus': return item.location.status;
                case 'networkState': return item.networkState;
                default: return item[property];
            }
        };
        this.dataSource.sort = this.sort;
        this.restaurantTable.renderRows()
        this.initMap();
        this.isLoading = false;
    }

    private initMap(): void {

        this.map = L.map('map', {
            center: [39.8282, -98.5795],
            zoom: 4
        });

        const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19
        });

        tiles.addTo(this.map);
        this.markerLayerGroup = L.layerGroup().addTo(this.map);

    }

    private addMarkers(): void {
        // First, remove all markers
        this.markerLayerGroup.clearLayers();

        if (this.dataSource.filteredData.length < 1000) {
            this.dataSource.filteredData.forEach(restaurant => {
                const latitude = Math.round(restaurant.location.latitude * 10)/10;
                const longitude = Math.round(restaurant.location.longitude * 10)/10;
                const marker = L.marker([latitude, longitude]).addTo(this.map);
                marker.bindPopup("<b>" + restaurant.location.number + "</b><br>" + restaurant.location.name);
                this.markerLayerGroup.addLayer(marker);
            });
        }
    }


    createDataSource() {
        let restaurantMap = new Map<string, Restaurant>();
        this.locations.forEach(location => {
            let restaurant: Restaurant = new Restaurant;
            restaurant.location = location;
            restaurantMap.set(location.number, restaurant);
        });

        this.merakiLocations.forEach(merakiLocation => {
            if (restaurantMap.has(merakiLocation.location.number)) {
                restaurantMap.get(merakiLocation.location.number).merakis = merakiLocation.merakis;
            }
        });

        this.fortigateLocations.forEach(fortigateLocation => {
            if (restaurantMap.has(fortigateLocation.location.number)) {
                restaurantMap.get(fortigateLocation.location.number).fortigates = fortigateLocation.fortigates;
            }
        });

        this.cradlepointLocations.forEach(cradlepointLocation => {
            if (restaurantMap.has(cradlepointLocation.location.number)) {
                restaurantMap.get(cradlepointLocation.location.number).cradlepoints = cradlepointLocation.cradlepoints;
            }
        });

        this.mistLocations.forEach(mistLocation => {
            if (restaurantMap.has(mistLocation.location.number)) {
                restaurantMap.get(mistLocation.location.number).mists = mistLocation.mists;
            }
        });

        restaurantMap.forEach((restaurant: Restaurant, storeNumber: string) => {
            restaurant.networkStateEvaluation = this.computeNetworkStateEvaluation(restaurant);
            restaurant.networkState = restaurant.networkStateEvaluation.state;
            this.restaurants.push(restaurant);
        });
    }

    /**
     * Chain-wide FortiGate data has no vpn_status, so a FortiGate failed over to
     * its backup WAN can't be detected the way Device Health does. Instead we
     * detect "on backup" the same way the RN-API's /wan/all-on-backup does: a WAN2
     * Cradlepoint whose IP matches the store's single online Meraki public IP
     * (i.e. the store is egressing over the LTE backup). This holds even when the
     * FortiGate is still reachable, which is why evaluateNetworkState() alone
     * (it gives an online FortiGate priority as ON_WAN1) misses these stores.
     */
    private computeNetworkStateEvaluation(restaurant: Restaurant): NetworkStateEvaluation {
        const backupWanCradlepoint = this.findBackupWanCradlepoint(restaurant);
        if (backupWanCradlepoint) {
            return buildNetworkStateEvaluation(
                NETWORK_STATE.ON_WAN2,
                'the store has failed over to the WAN2 backup circuit.',
                [
                    `The store's online Meraki switches all report public IP ${backupWanCradlepoint.ipv4_address}.`,
                    `That is the address of WAN2 CradlePoint ${backupWanCradlepoint.name}, so traffic is leaving through the LTE modem rather than the primary circuit.`,
                    'The chain-wide view reads a cache with no tunnel or interface data, so this comparison is the only failover signal available here.',
                ]
            );
        }
        return evaluateNetworkState({
            fortigates: restaurant.fortigates || [],
            cradlepoints: restaurant.cradlepoints || [],
            merakiSwitchStatuses: restaurant.merakis || [],
        });
    }

    private findBackupWanCradlepoint(restaurant: Restaurant): Cradlepoint | null {
        const merakiPublicIp = this.getOnlineMerakiPublicIp(restaurant.merakis || []);
        if (merakiPublicIp == null) {
            return null;
        }
        return (restaurant.cradlepoints || []).find(
            (cradlepoint: Cradlepoint) => cradlepoint.name?.includes('WAN2')
                && cradlepoint.ipv4_address === merakiPublicIp
        ) || null;
    }

    // Mirrors the RN-API's get_meraki_public_ips: returns the public IP only when
    // every online switch shares exactly one, otherwise null.
    private getOnlineMerakiPublicIp(merakis: MerakiSwitchStatus[]): string | null {
        const ips = new Set<string>();
        merakis.forEach((meraki: MerakiSwitchStatus) => {
            if (meraki.status === 'online' && meraki.publicIp) {
                ips.add(meraki.publicIp);
            }
        });
        return ips.size === 1 ? ips.values().next().value : null;
    }

    async getAllLocations() {
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];
                location.latitude = locations_json["core"]["physicalAddress"]["coordinates"]["latitude"];
                location.longitude = locations_json["core"]["physicalAddress"]["coordinates"]["longitude"];
                location.tempCloseFlag = locations_json["locationStatus"]["tempCloseFlag"]
                if (location.tempCloseFlag) {
                    location.tempCloseReason = locations_json["locationStatus"]["tempCloseReason"]
                } else {
                    location.tempCloseReason = ""
                }

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                this.locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
    }

    onToggle() {
        this.applyFilter(this.filterText);
    }

    applyFilter(filterValue: string) {
        this.dataSource.filterPredicate = (data: Restaurant, filter: string) => {
            let containsFilterValue: boolean = false;
            let isOnBackup: boolean = false;
            let isDualLte: boolean = false;
            let isHardDown: boolean = true;

            // Handle filter text filtering
            if (filter == "null"
                || data.location.number.toLowerCase().includes(filterValue)
                || data.location.name.toLowerCase().includes(filterValue)
                || data.location.timezone.toLowerCase().includes(filterValue)
                || data.location.status.toLowerCase().includes(filterValue)) {
                containsFilterValue = true;
            }

            // Handle stores actually running on a backup WAN path (FortiGate VPN on
            // WAN2, or Cradlepoint EM failover). OFFLINE/no-device stores are NOT on
            // backup -- counting them was inflating this filter to 500+ stores.
            if (this.showOnBackup) {
                isOnBackup = data.networkState === NETWORK_STATE.ON_WAN2
                    || data.networkState === NETWORK_STATE.EM_FAILOVER;
            }

            // Handle Dual LTE
            if (this.showDualLte && data.hasOwnProperty('cradlepoints')) {
                let foundWan1: boolean = false;
                let foundWan2: boolean = false;
                data.cradlepoints.forEach((cradlepoint: Cradlepoint) => {
                    if (cradlepoint.name.includes('WAN1')) {
                        foundWan1 = true;
                    } else if (cradlepoint.name.includes('WAN2')) {
                        foundWan2 = true
                    }
                });
                if (foundWan1 && foundWan2) {
                    isDualLte = true;
                }
            }

            // Handle hard down
            if (this.showHardDown) {
                if (data.hasOwnProperty('fortigates') && data.fortigates.length > 0) {
                    data.fortigates.forEach((fortigate: Fortigate) => {
                        if (fortigate.conn_status == 1) {
                            isHardDown = false;
                        }
                    });
                } else {
                    isHardDown = false;
                }
                if (data.hasOwnProperty('cradlepoints') && data.cradlepoints.length > 0) {
                    data.cradlepoints.forEach((cradlepoint: Cradlepoint) => {
                        if (cradlepoint.state == 'online') {
                            isHardDown = false;
                        }
                    });
                } else {
                    isHardDown = false;
                }
            } else {
                isHardDown = false;
            }

            // Coalesce and make filtering decision
            if (this.hideLabs && data.location.number.charAt(0) == "9") {
                return false;
            } else if (this.hideFutures && data.location.status == "future") {
                return false;
            } else if (this.hideRemodels && (data.location.status == "remodel" || data.location.tempCloseReason == "Remodel")) {
                return false;
            } else if (!containsFilterValue) {
                return false
            } else if (this.showOnBackup && !isOnBackup) {
                return false;
            } else if (this.showDualLte && !isDualLte) {
                return false;
            } else if (this.showHardDown && !isHardDown) {
                return false;
            } else {
                return true;
            }
        }

        if (filterValue == null || filterValue == "") { // handles case where no filter text is provided
            this.dataSource.filter = "null";
        } else {
            this.dataSource.filter = filterValue.trim().toLowerCase();
        }
        this.addMarkers();
    }

}